import React, { useState } from "react";
import { FaMapMarkerAlt, FaPlus } from "react-icons/fa";
import AddressForm from "../steps/components/AddressForm";
import SavedAddressCard from "../steps/components/SavedAddressCard";
import CheckoutEditModal from "../steps/components/CheckoutEditModal";

const CheckoutAddressCard = ({
  selectedAddress,
  userAddresses = [],
  selectedAddressId,
  onSelectAddress,
  onDeleteAddress,
  onEditAddress,
  onAddNewAddress,
  addressesLoading,
  pincodeStatus,
  pincodeMessage,
  pincodeData,
}) => {
  const [isChangingAddress, setIsChangingAddress] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const handleSelect = (addr) => {
    onSelectAddress(addr);
    setIsChangingAddress(false);
    setShowNewAddressForm(false);
  };

  const handleNewSuccess = (savedAddr) => {
    setShowNewAddressForm(false);
    setIsChangingAddress(false);
    onAddNewAddress(savedAddr);
  };

  const handleEditSubmit = async (formData) => {
    await onEditAddress(editingAddress, formData);
    setEditingAddress(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 font-bold text-gray-900 text-base">
          <FaMapMarkerAlt className="h-5 w-5 text-[#046a38]" />
          <span>Delivering to</span>
        </div>

        <button
          onClick={() => setIsChangingAddress(!isChangingAddress)}
          className="shrink-0 rounded-lg border border-[#046a38] px-4 py-2 text-xs sm:text-sm font-semibold text-[#046a38] transition hover:bg-emerald-50 active:scale-95"
        >
          {isChangingAddress ? "Done" : "Change Address"}
        </button>
      </div>

      <div className="mt-3 pt-2 border-t border-gray-100">
        {!selectedAddress ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-2">
            <div className="space-y-1 text-xs sm:text-sm text-gray-600">
              <p className="font-semibold text-gray-800">
                No delivery address selected
              </p>
              <p className="text-gray-500 text-xs">
                Please add or select a delivery address to proceed with checkout.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsChangingAddress(true);
                setShowNewAddressForm(true);
              }}
              className="shrink-0 self-start sm:self-center rounded-xl bg-[#046a38] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition hover:bg-[#03522b] active:scale-95"
            >
              + Add Address
            </button>
          </div>
        ) : !isChangingAddress ? (
          <div className="space-y-1.5 text-xs sm:text-sm text-gray-700">
            <div className="flex items-center gap-2 font-bold text-gray-900">
              <span>{selectedAddress?.address_type || "Home"}</span>
              {selectedAddress?.is_default && (
                <span className="rounded bg-[#046a38] px-1.5 py-0.5 text-[9px] font-bold text-white uppercase tracking-wider">
                  DEFAULT
                </span>
              )}
            </div>
            <p className="text-gray-700 font-medium">
              {selectedAddress?.name ? `${selectedAddress.name}, ` : ""}
              {selectedAddress?.address || selectedAddress?.street || ""}
              {selectedAddress?.locality ? `, ${selectedAddress.locality}` : ""}
              {selectedAddress?.city ? `, ${selectedAddress.city}` : ""}
              {selectedAddress?.pincode ? ` - ${selectedAddress.pincode}` : ""}
              {selectedAddress?.state ? `, ${selectedAddress.state}` : ""}
            </p>
            <p className="text-gray-600 font-medium">
              Mobile: {selectedAddress?.mobile || selectedAddress?.phone || "9876543210"}
            </p>

            {/* Serviceability Status Badge */}
            {pincodeStatus === "checking" && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium pt-1">
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Checking delivery availability…</span>
              </div>
            )}

            {pincodeStatus === "serviceable" && (
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#046a38] pt-1">
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                  <span>✓</span>
                  <span>Delivery available {pincodeData?.area_name ? `(${pincodeData.area_name})` : ""}</span>
                </span>
              </div>
            )}

            {(pincodeStatus === "not_serviceable" || pincodeStatus === "error") && (
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#ea580c] pt-1">
                <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-800">
                  <span>⚠️</span>
                  <span>Delivery not available for this pincode — change address</span>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                {showNewAddressForm ? "Add New Address" : "Select Delivery Address"}
              </p>
              <button
                onClick={() => {
                  setIsChangingAddress(false);
                  setShowNewAddressForm(false);
                }}
                className="text-xs font-bold text-[#046a38] hover:underline"
              >
                Done / Close
              </button>
            </div>

            {showNewAddressForm ? (
              <AddressForm
                onSuccess={handleNewSuccess}
                onCancel={() => setShowNewAddressForm(false)}
              />
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowNewAddressForm(true)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#046a38] bg-emerald-50/50 py-3 text-xs sm:text-sm font-bold text-[#046a38] transition hover:bg-emerald-100/60 active:scale-98"
                >
                  <FaPlus className="h-3.5 w-3.5" />
                  <span>+ Add New Address</span>
                </button>

                <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-1">
                  {userAddresses && userAddresses.length > 0 ? (
                    userAddresses.map((addr) => (
                      <SavedAddressCard
                        key={addr.address_id ?? addr.id}
                        addr={addr}
                        selectedId={selectedAddressId || (selectedAddress?.address_id ?? selectedAddress?.id)}
                        showForm={showNewAddressForm}
                        onSelect={() => handleSelect(addr)}
                        onEdit={() => setEditingAddress(addr)}
                        onDelete={() => onDeleteAddress(addr.address_id ?? addr.id)}
                      />
                    ))
                  ) : (
                    <p className="text-center text-xs text-gray-500 py-3">
                      No saved addresses found. Click above to add one.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {editingAddress && (
        <CheckoutEditModal
          address={editingAddress}
          loading={addressesLoading}
          onSubmit={handleEditSubmit}
          onCancel={() => setEditingAddress(null)}
        />
      )}
    </div>
  );
};

export default CheckoutAddressCard;
